/**
 * ISO-3166-1 alpha-2 → human country name lookup for the admin
 * metrics surface (geography section + cohort country filter).
 *
 * Scope: the codes Sill's edge actually observes — the country comes
 * from Cloudflare's `cf.country` on the edge `discovery_context.edge_meta`,
 * which can emit any ISO-3166-1 alpha-2 or the literal `'ZZ'` "unknown"
 * sentinel (see `AdminMetricsGeographyRow.geo_country`).
 *
 * This map covers the common-traffic surface — most countries with
 * meaningful agent / merchant population. Anything else falls through
 * to `countryName()` returning the raw code (e.g. `XK` → `XK`), so a
 * tail country still renders without us asserting a name we haven't
 * verified.
 *
 * `'ZZ'` is the unknown bucket; we render it as "Unknown" explicitly
 * at the call site, NOT in this map — keeping the map a pure ISO
 * lookup makes it auditable.
 *
 * No external dependency, no network call, no full-i18n library — a
 * static map is the right size for the staff console.
 */

const NAMES: Readonly<Record<string, string>> = {
  // Americas
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',
  BR: 'Brazil',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  VE: 'Venezuela',
  UY: 'Uruguay',

  // Europe
  GB: 'United Kingdom',
  IE: 'Ireland',
  FR: 'France',
  DE: 'Germany',
  ES: 'Spain',
  IT: 'Italy',
  PT: 'Portugal',
  NL: 'Netherlands',
  BE: 'Belgium',
  LU: 'Luxembourg',
  CH: 'Switzerland',
  AT: 'Austria',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  IS: 'Iceland',
  PL: 'Poland',
  CZ: 'Czechia',
  SK: 'Slovakia',
  HU: 'Hungary',
  RO: 'Romania',
  BG: 'Bulgaria',
  GR: 'Greece',
  HR: 'Croatia',
  SI: 'Slovenia',
  EE: 'Estonia',
  LV: 'Latvia',
  LT: 'Lithuania',
  UA: 'Ukraine',
  RS: 'Serbia',
  BA: 'Bosnia and Herzegovina',
  AL: 'Albania',
  MK: 'North Macedonia',
  ME: 'Montenegro',
  MD: 'Moldova',
  BY: 'Belarus',
  RU: 'Russia',
  TR: 'Türkiye',
  CY: 'Cyprus',
  MT: 'Malta',

  // Asia-Pacific
  CN: 'China',
  JP: 'Japan',
  KR: 'South Korea',
  KP: 'North Korea',
  TW: 'Taiwan',
  HK: 'Hong Kong',
  MO: 'Macao',
  SG: 'Singapore',
  MY: 'Malaysia',
  TH: 'Thailand',
  VN: 'Vietnam',
  PH: 'Philippines',
  ID: 'Indonesia',
  IN: 'India',
  PK: 'Pakistan',
  BD: 'Bangladesh',
  LK: 'Sri Lanka',
  NP: 'Nepal',
  MM: 'Myanmar',
  KH: 'Cambodia',
  LA: 'Laos',
  MN: 'Mongolia',
  AU: 'Australia',
  NZ: 'New Zealand',
  FJ: 'Fiji',

  // Middle East
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  IL: 'Israel',
  QA: 'Qatar',
  KW: 'Kuwait',
  BH: 'Bahrain',
  OM: 'Oman',
  JO: 'Jordan',
  LB: 'Lebanon',
  IQ: 'Iraq',
  IR: 'Iran',
  SY: 'Syria',
  YE: 'Yemen',
  PS: 'Palestine',

  // Africa
  ZA: 'South Africa',
  NG: 'Nigeria',
  EG: 'Egypt',
  MA: 'Morocco',
  TN: 'Tunisia',
  DZ: 'Algeria',
  LY: 'Libya',
  KE: 'Kenya',
  GH: 'Ghana',
  CI: 'Côte d’Ivoire',
  SN: 'Senegal',
  ET: 'Ethiopia',
  TZ: 'Tanzania',
  UG: 'Uganda',
  RW: 'Rwanda',
  CM: 'Cameroon',
  AO: 'Angola',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
  MZ: 'Mozambique',
  BW: 'Botswana',
  NA: 'Namibia',
  MG: 'Madagascar',
  MU: 'Mauritius',
};

/**
 * Resolve an ISO-3166-1 alpha-2 (or `'ZZ'`) to a human label.
 *
 * - `'ZZ'`  → `'Unknown'` (the edge sentinel for "no geo data").
 * - Known ISO codes (case-insensitive) → the English country name
 *   from the static map above.
 * - Anything else → the input code uppercased (lets a rare country
 *   render gracefully without us asserting a name we haven't curated).
 *
 * The function is pure; callers can memoise at the section level.
 */
export function countryName(code: string): string {
  if (code === 'ZZ') return 'Unknown';
  const upper = code.toUpperCase();
  return NAMES[upper] ?? upper;
}

/**
 * Render the regional-indicator flag emoji for an ISO-3166-1 alpha-2
 * code (e.g. `'US'` → `'🇺🇸'`). Returns the empty string for `'ZZ'`
 * or any value that isn't two ASCII letters — the geography section
 * shows a small globe placeholder in those cases rather than a broken
 * glyph.
 *
 * Implementation: each letter maps to the corresponding
 * Regional Indicator Symbol code-point (`U+1F1E6` for `'A'` …
 * `U+1F1FF` for `'Z'`); the browser composes them into the flag.
 *
 * No font-shaping guarantee: on platforms without flag emoji
 * (Windows default), the two letters render as plain RIS glyphs —
 * still readable, no layout damage. We never assert "this WILL show
 * a flag," just "this is a stable, locale-free code point pair."
 */
export function countryFlagEmoji(code: string): string {
  if (code === 'ZZ') return '';
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return '';
  const A = 'A'.charCodeAt(0);
  const BASE = 0x1f1e6;
  const cp0 = BASE + (upper.charCodeAt(0) - A);
  const cp1 = BASE + (upper.charCodeAt(1) - A);
  return String.fromCodePoint(cp0, cp1);
}
